import { Box } from '@mantine/core';
import { useOutlet } from 'react-router';
import cn from 'classnames';
import type {
  NavigationLayout,
  NavigationLayoutProps,
} from '@ayocore/platform/client/types/navigation-item.type';
import classes from './base_layout/BaseLayout.module.css';

type ContainerLayoutProps = NavigationLayoutProps;

/**
 * Базовый контейнерный layout (системные отступы и фон BaseLayout).
 * Не рендерит контейнер, если Outlet не имеет совпадающего дочернего маршрута.
 */
export const DefaultContainerLayout: NavigationLayout = ({
  isFirstActive,
  activeTabTouchesRight,
}: ContainerLayoutProps) => {
  const outlet = useOutlet();
  if (!outlet) return null;

  return (
    <Box
      className={cn(classes.content, {
        [classes.contentNoTopLeftRadius]: isFirstActive,
        [classes.contentNoTopRightRadius]: activeTabTouchesRight,
      })}
    >
      {outlet}
    </Box>
  );
};

/**
 * Контейнер без внутренних отступов и скруглений.
 * Сохраняет левый системный внешний отступ, чтобы контент совпадал с табами.
 */
export const NoPaddingContainerLayout: NavigationLayout = ({
  children,
  isFirstActive,
  activeTabTouchesRight,
}: ContainerLayoutProps) => (
  <Box
    className={cn(
      classes.content,
      classes.contentNoPadding,
      classes.contentNoRadius,
      classes.contentNoMarginRight,
      classes.contentNoMarginBottom,
      {
        [classes.contentNoTopLeftRadius]: isFirstActive,
        [classes.contentNoTopRightRadius]: activeTabTouchesRight,
      }
    )}
  >
    {children}
  </Box>
);

/**
 * NoPadding-вариант с белым фоном контейнера.
 */
export const NoPaddingWhiteContainerLayout: NavigationLayout = ({
  children,
  isFirstActive,
  activeTabTouchesRight,
}: ContainerLayoutProps) => (
  <Box
    className={cn(
      classes.content,
      classes.contentNoPadding,
      classes.contentNoRadius,
      classes.contentNoMarginRight,
      classes.contentNoMarginBottom,
      classes.contentWhiteBg,
      {
        [classes.contentNoTopLeftRadius]: isFirstActive,
        [classes.contentNoTopRightRadius]: activeTabTouchesRight,
      }
    )}
  >
    {children}
  </Box>
);

/**
 * Контейнер для приветственных/фоновых экранов без белого блока BaseLayout.
 * Рендерит только содержимое маршрута.
 *
 * ⚠️ Не импортировать в файлы, загружаемые до Mantine CSS (напр. AppRuntime.ts) —
 * этот модуль тянет BaseLayout.module.css и ломает CSS каскад glassmorphism.
 * Для AppRuntime используется WelcomePassthroughLayout (аналог без импорта CSS).
 */
export const WelcomeContainerLayout: NavigationLayout = ({ children }: ContainerLayoutProps) => (
  <>{children}</>
);
